import { useTheme } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { FC } from "react";

import { colors } from "@/Components/v2/utils/brand-colors";
import { MarkData } from "@/pages/investment-levels/types/saturationCurve";

export interface CurveDataType {
  id: string;
  color: string;
  curveCoordinates: [number, number][];
  markData: MarkData[];
}

interface WeeklySpendChartProps {
  title?: string;
  curves: CurveDataType[] | undefined;
  markerMap: MarkerMap;
  onCurveClick?: (curveId: string) => void;
  tooltipFormatter?: (params: any[]) => string;
  xAxisFormatter?: (value: number) => string;
  yAxisFormatter?: (value: number) => string;
}

export interface MarkerMap {
  [key: string]: {
    color: string;
  };
}

const generateSeries = (
  curves: CurveDataType[],
  markerMap: MarkerMap,
  tooltipFormatter?: (params: any[]) => string
) => {
  return curves.map((curve) => {
    const markers = curve.markData.map((mark: MarkData) => ({
      coord: mark.markCoordinates,
      name: mark.name,
      itemStyle: {
        color: colors.white,
        borderColor: markerMap[mark.key as keyof typeof markerMap]?.color,
        borderWidth: 3,
      },
      tooltip: {
        formatter: (params: any) => {
          return tooltipFormatter ? tooltipFormatter([params]) : "";
        },
      },
    }));

    return {
      id: curve.id,
      type: "line",
      data: curve.curveCoordinates,
      lineStyle: { color: curve.color },
      symbol: "none",
      smooth: true,
      triggerLineEvent: true,
      markPoint: {
        data: markers,
        symbol: "circle",
        symbolSize: 12,
        tooltip: {
          trigger: "item",
        },
      },
      zIndex: 1,
      curve,
      emphasis: {
        focus: "series",
      },
    };
  });
};

const ChartWithMarkers: FC<WeeklySpendChartProps> = ({
  curves,
  markerMap,
  // onCurveClick,
  title,
  tooltipFormatter,
  xAxisFormatter,
  yAxisFormatter,
}) => {
  if (!curves) return null;

  const theme = useTheme();
  // Chart options
  const options = {
    tooltip: tooltipFormatter
      ? {
          trigger: "axis",
          backgroundColor: theme.palette.echartTooltip.background,
          textStyle: {
            color: theme.palette.echartTooltip.textColor,
          },
          formatter: tooltipFormatter,
        }
      : false,
    textStyle: {
      color: theme.palette.echartTooltip.textColor,
    },
    title: {
      text: title,
      left: "center",
      bottom: 20,
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 12,
      },
    },
    xAxis: {
      type: "value",
      min: 0,
      splitLine: {
        show: true,
        lineStyle: {
          type: "dotted",
        },
      },
      axisLabel: {
        formatter: xAxisFormatter,
      },
    },
    yAxis: {
      type: "value",
      splitLine: { show: false },
      min: 0,
      axisLabel: {
        formatter: yAxisFormatter,
      },
    },
    series: generateSeries(curves, markerMap, tooltipFormatter),
  };

  return (
    <ReactECharts
      option={options}
      style={{ height: 600, width: "100%" }}
      // Fix animation issue when using onEvents
      // onEvents={onEvents}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default ChartWithMarkers;
